"use client";

import React from "react";
import { ArrowUpRight, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Reveal } from "@/components/cosmic/Reveal";
interface StaticArticle {
  id: string;
  title: string;
  excerpt: string;
  permalink?: string;
  image?: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const fallbackArticles: StaticArticle[] = [{
  id: "fallback-1",
  title: "Mapping the Inflationary Horizon",
  excerpt: "Cosmic microwave background anomalies offer tantalizing boundary constraints on early universe bubble collisions.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/gravitational_lensing_ring.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/gravitational_lensing_ring.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/gravitational_lensing_ring.png-wvc-sizes",
    alt: "Gravitational lensing ring distorting cosmic light"
  }
}, {
  id: "fallback-2",
  title: "Macroscopic Quantum Coherence",
  excerpt: "Experimental laboratories push superposition principles toward larger molecular thresholds in cryogenic vacuums.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/quantum_quasar_bell_experiment.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/quantum_quasar_bell_experiment.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/quantum_quasar_bell_experiment.webp-wvc-sizes",
    alt: "Quantum optical experiment with laser beams and cryogenic vacuum chambers"
  }
}, {
  id: "fallback-3",
  title: "Gravitational Wave Spectroscopy",
  excerpt: "Interferometer data from colliding black holes reveals subtle vibrational echoes predicted by string geometry.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/jwst_deep_field_early_galaxies.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/jwst_deep_field_early_galaxies.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/jwst_deep_field_early_galaxies.png-wvc-sizes",
    alt: "Deep field cosmic view showing ancient distant galaxies"
  }
}];
function DiscoveriesConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const displayItems: StaticArticle[] = !loading && posts && posts.length > 0 ? posts.slice(0, 3).map((post, index) => ({
    id: post.id || `post-${index}`,
    title: post.title || fallbackArticles[index % fallbackArticles.length].title,
    excerpt: post.excerpt ? post.excerpt.replace(/<[^>]+>/g, "").trim() : fallbackArticles[index % fallbackArticles.length].excerpt,
    permalink: post.link || undefined,
    image: post.featuredImage ? {
      src: post.featuredImage,
      srcset: "",
      sizes: "",
      alt: post.title || "Cosmic observation dispatch"
    } : fallbackArticles[index % fallbackArticles.length].image
  })) : fallbackArticles;
  return <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-x-auto pb-4 md:pb-0 snap-x snap-mandatory scrollbar-thin max-w-full">
      {displayItems.map((article, index) => {
      return <div key={article.id} data-index={index} className="w-[calc(100vw-3rem)] max-w-[340px] sm:w-[340px] md:w-auto shrink-0 snap-center h-full min-w-0">
            <div className="ql-card h-full flex flex-col justify-between group">
              <div>
                {article.image && <div className="aspect-[16/10] w-full overflow-hidden border-b border-border bg-card/60">
                    <img src={article.image.src} {...article.image.srcset ? {
                "data-wvc-srcset": article.image.srcset
              } : {}} {...article.image.sizes ? {
                "data-wvc-sizes": article.image.sizes
              } : {}} alt={article.image.alt} width={1600} height={1000} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>}
                <div className="p-6">
                  <h3 className="font-serif text-xl font-bold tracking-wide text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {article.excerpt}
                  </p>
                </div>
              </div>
              <div className="px-6 pb-6 pt-2 border-t border-border/40 flex items-center justify-between">
                {article.permalink ? <Link href={article.permalink} className="inline-flex items-center gap-1.5 text-xs font-serif uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors">
                    <span>Read Dispatch</span>
                    <ArrowUpRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link> : <Link to="/CosmicDiscoveriesPage" className="inline-flex items-center gap-1.5 text-xs font-serif uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors">
                    <span>Read Dispatch</span>
                    <ArrowUpRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>}
                <span className="font-mono text-[10px] text-muted-foreground uppercase">
                  DISPATCH 0{index + 1}
                </span>
              </div>
            </div>
          </div>;
    })}
    </div>;
}
export default function CosmicDiscoveries() {
  return <section id="cosmic-discoveries" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground">
      <div className="relative z-10 mx-auto max-w-[76rem] w-full px-4 sm:px-6">
        {/* Subtle section hairline divider */}
        <div className="ql-divider mb-16" aria-hidden="true" />

        {/* Section Header */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 mb-3 font-mono text-xs font-semibold tracking-widest text-primary uppercase">
              <Compass className="size-3.5" />
              <span>PEER-REVIEWED ARCHIVES</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-wide text-foreground leading-[1.12]">
              Cosmic Discoveries
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Recent papers, telescope observations, and theoretical breakthroughs decoded by science communicators.
            </p>
          </div>
        </Reveal>

        {/* Dynamic / Fallback Card Carousel Grid */}
        <Reveal delay={120}>
          <div className="pb-12 w-full max-w-full overflow-x-clip">
            <PostsProvider wp_query={{
            post_type: "post",
            per_page: 3,
            order: "desc",
            orderby: "date",
            status: ["publish"]
          }} className="w-full max-w-full">
              <DiscoveriesConsumer />
            </PostsProvider>
          </div>
        </Reveal>

        {/* Section CTA */}
        <div className="flex justify-center w-full">
          <Link to="/CosmicDiscoveriesPage" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center">
            <span>View All Articles</span>
          </Link>
        </div>
      </div>
    </section>;
}